/**
 * Synthetic data generator for development.
 * Creates a believable liquor/adult beverage intelligence field
 * with realistic cluster names, relationships, and spatial layout.
 *
 * This is replaced by real SERP data in production.
 */

/**
 * EDITORIAL COMPOSITION
 *
 * This is not neutral data. This is an authored argument about
 * the liquor/beverage intelligence landscape.
 *
 * Each category carries editorial weight:
 *   - gravitationalMass: how much space it claims, how many particles it attracts
 *   - spatialRadius: how tightly its clusters are packed
 *   - character: 'dominant' | 'entrenched' | 'emerging' | 'diffuse' | 'fragile' | 'peripheral' | 'structural'
 *
 * These are defensible editorial decisions, not computed metrics.
 * The field ARGUES a position. It does not merely display.
 */

export interface CategoryDefinition {
  name: string;
  hue: number;
  gravitationalMass: number;    // 0.2–1.0: how much of the field this category claims
  spatialRadius: number;        // how tightly clusters are packed (smaller = denser core)
  character: 'dominant' | 'entrenched' | 'emerging' | 'diffuse' | 'fragile' | 'peripheral' | 'structural';
  clusters: { name: string; weight: number; trend: 'rising' | 'stable' | 'fading' | 'emerging' }[];
}

// Loading placeholder categories — shown briefly while live data loads from Neon.
// Structure must stay intact (7 categories, cluster counts preserved) because
// create.ts, validation.ts, learn-interaction.ts reference by catIdx/clusterIdx.
export const CATEGORIES: CategoryDefinition[] = [
  {
    name: 'Cultural Intelligence',
    hue: 0.50,
    gravitationalMass: 1.0,
    spatialRadius: 120,
    character: 'dominant',
    clusters: [
      { name: 'Cultural Momentum', weight: 0.95, trend: 'rising' },
      { name: 'Brand Activation Formats', weight: 0.85, trend: 'rising' },
      { name: 'Social Amplification', weight: 0.90, trend: 'rising' },
      { name: 'Live Event Intelligence', weight: 0.80, trend: 'rising' },
      { name: 'Immersive Experiences', weight: 0.70, trend: 'rising' },
      { name: 'Community Integration', weight: 0.75, trend: 'stable' },
      { name: 'Talent Partnerships', weight: 0.65, trend: 'rising' },
      { name: 'Creator Economy', weight: 0.60, trend: 'rising' },
    ],
  },
  {
    name: 'Market Intelligence',
    hue: 0.09,
    gravitationalMass: 0.85,
    spatialRadius: 180,
    character: 'entrenched',
    clusters: [
      { name: 'Competitive Intelligence', weight: 0.80, trend: 'stable' },
      { name: 'Audience Behavior', weight: 0.90, trend: 'rising' },
      { name: 'Consumer Sentiment', weight: 0.85, trend: 'rising' },
      { name: 'Hyperlocal Insights', weight: 0.75, trend: 'stable' },
      { name: 'ROI Measurement', weight: 0.70, trend: 'rising' },
      { name: 'Brand Perception', weight: 0.55, trend: 'emerging' },
      { name: 'Share of Voice', weight: 0.50, trend: 'stable' },
      { name: 'Category Trends', weight: 0.65, trend: 'stable' },
      { name: 'Market Signals', weight: 0.60, trend: 'rising' },
      { name: 'Industry Intelligence', weight: 0.30, trend: 'emerging' },
    ],
  },
  {
    name: 'Audience Signals',
    hue: 0.36,
    gravitationalMass: 0.70,
    spatialRadius: 140,
    character: 'emerging',
    clusters: [
      { name: 'Participation Patterns', weight: 0.85, trend: 'rising' },
      { name: 'Engagement Velocity', weight: 0.75, trend: 'rising' },
      { name: 'Dwell Behavior', weight: 0.70, trend: 'stable' },
      { name: 'Conversion Signals', weight: 0.60, trend: 'rising' },
      { name: 'Community Resonance', weight: 0.80, trend: 'rising' },
      { name: 'Loyalty Mechanics', weight: 0.45, trend: 'emerging' },
      { name: 'FOMO Dynamics', weight: 0.55, trend: 'rising' },
      { name: 'Exclusivity Appeal', weight: 0.50, trend: 'stable' },
      { name: 'UGC Velocity', weight: 0.35, trend: 'emerging' },
    ],
  },
  {
    name: 'Experience Design',
    hue: 0.76,
    gravitationalMass: 0.60,
    spatialRadius: 250,
    character: 'diffuse',
    clusters: [
      { name: 'Immersive Formats', weight: 0.65, trend: 'rising' },
      { name: 'Sampling Ecosystems', weight: 0.55, trend: 'stable' },
      { name: 'Content-First Activations', weight: 0.40, trend: 'emerging' },
      { name: 'Spatial Design', weight: 0.50, trend: 'rising' },
      { name: 'Digital-Physical Hybrid', weight: 0.45, trend: 'stable' },
      { name: 'Brand Houses', weight: 0.55, trend: 'rising' },
      { name: 'Pop-Up Experiences', weight: 0.60, trend: 'fading' },
      { name: 'Festival Activations', weight: 0.35, trend: 'stable' },
      { name: 'Retail Theater', weight: 0.45, trend: 'rising' },
      { name: 'Live Commerce', weight: 0.30, trend: 'emerging' },
      { name: 'Mobile Tours', weight: 0.40, trend: 'stable' },
    ],
  },
  {
    name: 'Tech & Innovation',
    hue: 0.03,
    gravitationalMass: 0.50,
    spatialRadius: 200,
    character: 'fragile',
    clusters: [
      { name: 'AR Experiences', weight: 0.75, trend: 'rising' },
      { name: 'AI Personalization', weight: 0.60, trend: 'rising' },
      { name: 'Spatial Computing', weight: 0.40, trend: 'emerging' },
      { name: 'Biometric Engagement', weight: 0.50, trend: 'stable' },
      { name: 'Real-Time Optimization', weight: 0.65, trend: 'stable' },
      { name: 'IoT Integration', weight: 0.55, trend: 'fading' },
      { name: 'Voice Activation', weight: 0.35, trend: 'stable' },
      { name: 'Digital Twins', weight: 0.45, trend: 'emerging' },
    ],
  },
  {
    name: 'Social & Creator',
    hue: 0.90,
    gravitationalMass: 0.40,
    spatialRadius: 160,
    character: 'peripheral',
    clusters: [
      { name: 'TikTok Creators', weight: 0.55, trend: 'rising' },
      { name: 'Instagram Partnerships', weight: 0.60, trend: 'stable' },
      { name: 'Micro-Influencers', weight: 0.50, trend: 'rising' },
      { name: 'Artist Collaborations', weight: 0.45, trend: 'stable' },
      { name: 'Athlete Sponsorships', weight: 0.35, trend: 'emerging' },
      { name: 'Brand Ambassadors', weight: 0.40, trend: 'stable' },
      { name: 'Celebrity Partnerships', weight: 0.50, trend: 'rising' },
      { name: 'Creator Economy', weight: 0.55, trend: 'rising' },
    ],
  },
  {
    name: 'Performance & ROI',
    hue: 0.61,
    gravitationalMass: 0.35,
    spatialRadius: 180,
    character: 'structural',
    clusters: [
      { name: 'Sales Attribution', weight: 0.65, trend: 'rising' },
      { name: 'Brand Lift Studies', weight: 0.70, trend: 'rising' },
      { name: 'Cost Per Engagement', weight: 0.40, trend: 'stable' },
      { name: 'Earned Media Value', weight: 0.55, trend: 'stable' },
      { name: 'Purchase Intent', weight: 0.50, trend: 'stable' },
      { name: 'NPS Tracking', weight: 0.45, trend: 'emerging' },
    ],
  },
];

// Seeded random for reproducibility
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

export interface SyntheticNode {
  id: string;
  name: string;
  category: string;
  position: [number, number, number];
  scale: number;
  color: [number, number, number]; // RGB 0–1
  intensity: number;
  velocity: number;
  trend: 'rising' | 'stable' | 'fading' | 'emerging';
  flags: number; // bitfield
}

export interface SyntheticEdge {
  source: number; // index into nodes array
  target: number;
  weight: number;
}

export interface SyntheticField {
  nodes: SyntheticNode[];
  edges: SyntheticEdge[];
}

/**
 * HSL to RGB conversion
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [r, g, b];
}

/**
 * Loading placeholder field — shown briefly while live data fetches.
 * Generic gravitational clusters, no domain-specific content.
 * Replaced immediately by transformLiveData() once the gateway responds.
 */
export function generateSyntheticField(seed: number = 42): SyntheticField {
  const rand = seededRandom(seed);
  const nodes: SyntheticNode[] = [];
  const edges: SyntheticEdge[] = [];

  let nodeIndex = 0;

  // AUTHORED spatial positions — NOT uniform sphere distribution.
  // This is a composed argument, not a physics simulation.
  const composedPositions: Record<string, [number, number, number]> = {
    'Ready To Drink':         [0, 40, -20],       // Center stage — the dominant mass
    'Premium Spirits':        [-280, -60, 80],     // Lower left — entrenched anchor
    'No/Low Alcohol':         [260, 100, -100],    // Upper right — emerging challenger (faces Premium across the void)
    'Experience & Culture':   [-120, 220, 160],    // Above and behind — vast diffuse territory
    'Demographics & Markets': [160, -180, 120],    // Lower right — fragile, fractured
    'Ingredients & Innovation': [-200, 140, -220], // Peripheral — orbiting edge
    'Distribution & Commerce': [80, -100, -200],   // Below center — structural skeleton
  };

  CATEGORIES.forEach((category, catIdx) => {
    const catCenter = composedPositions[category.name] || [0, 0, 0];
    const spatialR = category.spatialRadius;

    category.clusters.forEach((cluster, clusterIdx) => {
      // Cluster position within category territory
      const offset: [number, number, number] = [
        catCenter[0] + (rand() - 0.5) * spatialR,
        catCenter[1] + (rand() - 0.5) * spatialR,
        catCenter[2] + (rand() - 0.5) * spatialR,
      ];

      // Use the AUTHORED weight, not random signal strength
      const signalStrength = cluster.weight;
      const trend = cluster.trend;
      const flags = trend === 'emerging' ? 8 : 0;

      // Color: deeply chromatic. Mass determines brightness.
      // Heavy clusters burn brighter. Peripheral ones are deeper/darker.
      const hue = (category.hue + (rand() - 0.5) * 0.04 + 1.0) % 1.0;
      const sat = 0.82 + rand() * 0.13;
      const massInfluence = category.gravitationalMass;
      const lit = 0.35 + signalStrength * 0.15 + massInfluence * 0.08;
      const color = hslToRgb(hue, sat, lit);

      nodes.push({
        id: `cluster-${catIdx}-${clusterIdx}`,
        name: cluster.name,
        category: category.name,
        position: offset,
        scale: 0.5 + signalStrength * 1.5,
        color,
        intensity: signalStrength,
        velocity: rand() * 2 - 0.5,
        trend,
        flags,
      });

      // Satellite count scaled by cluster weight AND category mass
      // Heavy categories with strong clusters produce more sub-signals
      const satelliteCount = Math.floor(2 + cluster.weight * massInfluence * 10);
      for (let s = 0; s < satelliteCount; s++) {
        const satSpread = 30 + (1 - signalStrength) * 50;
        const satOffset: [number, number, number] = [
          offset[0] + (rand() - 0.5) * satSpread,
          offset[1] + (rand() - 0.5) * satSpread,
          offset[2] + (rand() - 0.5) * satSpread,
        ];

        const satIntensity = signalStrength * (0.3 + rand() * 0.4);
        const satColor = hslToRgb(
          (hue + (rand() - 0.5) * 0.03 + 1.0) % 1.0,
          sat * 0.95,
          lit * 0.88,
        );

        nodes.push({
          id: `sat-${catIdx}-${clusterIdx}-${s}`,
          name: `${cluster.name} (sub-signal ${s + 1})`,
          category: category.name,
          position: satOffset,
          scale: 0.3 + satIntensity * 0.8,
          color: satColor,
          intensity: satIntensity,
          velocity: rand() * 1.5 - 0.3,
          trend: rand() > 0.7 ? trend : 'stable',
          flags: 0,
        });

        edges.push({
          source: nodeIndex + 1 + s,
          target: nodeIndex,
          weight: 0.5 + rand() * 0.5,
        });
      }

      nodeIndex += 1 + satelliteCount;
    });
  });

  // Add cross-category edges (weaker connections between related concepts)
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (nodes[i].category !== nodes[j].category) {
        const dx = nodes[i].position[0] - nodes[j].position[0];
        const dy = nodes[i].position[1] - nodes[j].position[1];
        const dz = nodes[i].position[2] - nodes[j].position[2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        // Connect nodes that are spatially close despite being in different categories
        if (dist < 150 && rand() > 0.7) {
          edges.push({
            source: i,
            target: j,
            weight: 0.2 + rand() * 0.3,
          });
        }
      }
    }
  }

  return { nodes, edges };
}

/**
 * Pack synthetic nodes into typed arrays for GPU upload.
 * Returns buffers matching the architecture spec's per-instance layout.
 */
export function packFieldData(field: SyntheticField) {
  const count = field.nodes.length;

  // Per-instance buffers
  const positions = new Float32Array(count * 3);
  const scales = new Float32Array(count);
  const colors = new Float32Array(count * 3);
  const intensities = new Float32Array(count);
  const flags = new Float32Array(count);

  field.nodes.forEach((node, i) => {
    positions[i * 3] = node.position[0];
    positions[i * 3 + 1] = node.position[1];
    positions[i * 3 + 2] = node.position[2];

    scales[i] = node.scale;

    colors[i * 3] = node.color[0];
    colors[i * 3 + 1] = node.color[1];
    colors[i * 3 + 2] = node.color[2];

    intensities[i] = node.intensity;
    flags[i] = node.flags;
  });

  return { positions, scales, colors, intensities, flags, count };
}
